import { useState, useRef, useEffect } from "react"
import { motion } from "framer-motion"

const TextArea = ({
  id,
  label,
  variant = "outlined",
  error = false,
  helperText,
  fullWidth = false,
  required = false,
  disabled = false,
  value,
  onChange,
  onFocus,
  onBlur,
  rows = 4,
  className,
  ...props
}) => {
  const [focused, setFocused] = useState(false)
  const [hasValue, setHasValue] = useState(Boolean(value))
  const textareaRef = useRef(null)
  const labelRef = useRef(null)
  const [labelWidth, setLabelWidth] = useState(0)

  // Update hasValue when value prop changes
  useEffect(() => {
    setHasValue(Boolean(value))
  }, [value])

  // Measure label width for the notch
  useEffect(() => {
    if (labelRef.current) {
      setLabelWidth(labelRef.current.offsetWidth)
    }
  }, [label, required])

  const handleFocus = (e) => {
    setFocused(true)
    if (onFocus) onFocus(e)
  }

  const handleBlur = (e) => {
    setFocused(false)
    if (onBlur) onBlur(e)
  }

  const handleChange = (e) => {
    setHasValue(e.target.value.length > 0)
    if (onChange) onChange(e)
  }

  // Determine border color based on state
  const getBorderColor = () => {
    if (error) return "border border-red-500"
    if (focused) return "border border-blue-500"
    return "border border-gray-300"
  }

  // Determine label color based on state
  const getLabelColor = () => {
    if (error) return "text-red-500"
    if (focused) return "text-blue-500"
    return "text-gray-500"
  }

  return (
    <div className={`relative ${fullWidth ? "w-full" : "w-72"} ${className || ""}`}>
      <div className="relative">
        {/* Border with notch */}
        <div className={`relative ${disabled ? "bg-gray-100" : "bg-white"}`}>
          {/* This is the textarea field */}
          <textarea
            ref={textareaRef}
            id={id}
            name={props.name}
            value={value}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            required={required}
            rows={rows}
            className={`w-full px-3 py-4 pt-6 outline-none bg-transparent text-gray-900 resize-none ${
              disabled ? "text-gray-500 cursor-not-allowed" : ""
            } relative z-10`} // z-10 to ensure it's above the border
            {...props}
          />

          {/* This creates the border with a gap for the label */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Top border with gap for label */}
            <div className="absolute top-0 left-0 right-0 flex items-center">
              {/* Left part of top border */}
              <div
                className={`h-[1px] ${focused ? "h-[2px]" : "h-[1px]"} ${getBorderColor()} transition-all duration-200`}
                style={{
                  width: focused || hasValue ? "8px" : "100%",
                }}
              />

              {/* Gap for label - only show when focused or has value */}
              {(focused || hasValue) && <div style={{ width: `${labelWidth * 0.75 + 16}px` }} />}

              {/* Right part of top border */}
              <div
                className={`h-[1px] ${focused ? "h-[2px]" : "h-[1px]"} ${getBorderColor()} transition-all duration-200 flex-grow`}
              />
            </div>

            {/* Left border */}
            <div
              className={`absolute top-0 left-0 bottom-0 w-[1px] ${focused ? "w-[2px]" : "w-[1px]"} ${getBorderColor()} transition-all duration-200`}
            />

            {/* Right border */}
            <div
              className={`absolute top-0 right-0 bottom-0 w-[1px] ${focused ? "w-[2px]" : "w-[1px]"} ${getBorderColor()} transition-all duration-200`}
            />

            {/* Bottom border */}
            <div
              className={`absolute bottom-0 left-0 right-0 h-[1px] ${focused ? "h-[2px]" : "h-[1px]"} ${getBorderColor()} transition-all duration-200`}
            />
          </div>

          {/* Label */}
          <motion.label
            ref={labelRef}
            htmlFor={id}
            className={`absolute left-3 font-popinsMedium text-sm pointer-events-none ${getLabelColor()} ${
              disabled ? "text-gray-400" : ""
            } z-20 bg-white px-1`} // z-20 to ensure it's above everything and bg-white to hide the border
            initial={false}
            animate={{
              y: focused || hasValue ? -12 : 0,
              scale: focused || hasValue ? 0.75 : 1,
              x: focused || hasValue ? -4 : 0,
            }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </motion.label>
        </div>
      </div>

      {/* Helper text or error message */}
      {helperText && <p className={`mt-1 text-xs ${error ? "text-red-500" : "text-gray-500"}`}>{helperText}</p>}
    </div>
  )
}

export default TextArea
